import { createTheme } from '@mui/material/styles';

/**
 * Aurum - the GoToBed design system.
 *
 * The accent is Tangerine orange. The key is still named `gold` because it is spelled out
 * across ~130 call sites; renaming it is a separate mechanical change, not a colour one.
 *
 * Two values, and they are not interchangeable:
 *   gold.fill - surfaces and borders only (buttons, rules, frames, the logo). 2.77:1 on
 *               white, so it can never carry type - but nothing reads *through* a button,
 *               it reads *on* it, and ink on this fill is 6.15:1.
 *   gold.text - the only value safe to read as type (4.52:1 on white, WCAG AA).
 * Never set gold.fill on text.
 *
 * gold.deep carries two jobs at once - hover *text* on white (AurumHeader, NavBar, MiniCart,
 * MuiLink) and hover *surface* under white text (MaterialFilterBar, contained buttons). The
 * current value is tuned for the brightness of the hover, not for contrast: it measures
 * 2.86:1 in both directions, below the 4.5:1 those roles need. #A34D08 clears 5.80:1 both
 * ways if the hover states are ever revisited.
 */
export const gold = {
  fill: '#F57819',
  text: '#F57819', //'#BF590C',
  deep: '#A34D08',
  soft: 'rgba(245, 120, 25, 0.14)'
};

const serif = "'Playfair Display', Georgia, 'Times New Roman', serif";
const sans =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const ink = '#1D1C1A';
const inkMuted = '#5D5B56';
const inkFaint = '#8E8C86';
const hairline = '#ECEBE7';
const hairlineStrong = '#DEDCD6';

const heading = (fontSize, lineHeight = 1.1) => ({
  fontFamily: serif,
  fontWeight: 500,
  letterSpacing: '-0.01em',
  lineHeight,
  fontSize
});

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: gold.fill, dark: gold.deep, contrastText: ink },
    secondary: { main: ink, dark: '#000000', contrastText: '#FFFFFF' },
    gold,
    // One price colour on every surface: grid, detail, cart, checkout.
    price: { main: ink, sale: gold.text },
    // tile - the neutral surface a control settles onto when hovered or filled.
    background: { default: '#FFFFFF', paper: '#F8F8F6', tile: '#F2F2EF' },
    text: { primary: ink, secondary: inkMuted, disabled: inkFaint },
    divider: hairline,
    hairline: { main: hairline, strong: hairlineStrong },
    success: { main: '#2E7D32' },
    error: { main: '#B3261E' }
  },

  shape: { borderRadius: 2 },

  typography: {
    fontFamily: sans,
    h1: heading('clamp(2.2rem, 4.4vw, 3.4rem)', 1.04),
    h2: heading('clamp(1.75rem, 3.2vw, 2.5rem)'),
    h3: heading('2rem'),
    h4: heading('1.6rem'),
    h5: heading('1.3rem'),
    h6: heading('1.1rem', 1.2),
    subtitle1: { fontFamily: sans, fontWeight: 600, fontSize: '0.95rem' },
    subtitle2: { fontFamily: sans, fontWeight: 600, fontSize: '0.82rem' },
    body1: { fontFamily: sans, fontSize: '0.95rem', lineHeight: 1.65 },
    body2: { fontFamily: sans, fontSize: '0.87rem', lineHeight: 1.6 },
    caption: { fontFamily: sans, fontSize: '0.78rem', color: inkMuted },
    button: {
      fontFamily: sans,
      textTransform: 'uppercase',
      letterSpacing: '0.12em',
      fontWeight: 700,
      fontSize: '0.78rem'
    },
    // The small gold eyebrow labels used across the design.
    overline: {
      fontFamily: sans,
      fontSize: '0.69rem',
      letterSpacing: '0.24em',
      fontWeight: 600,
      textTransform: 'uppercase',
      lineHeight: 1.6
    }
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#FFFFFF',
          color: ink,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale'
        },
        a: { color: 'inherit', textDecoration: 'none' }
      }
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 2,
          paddingTop: 12,
          paddingBottom: 12,
          paddingLeft: 26,
          paddingRight: 26
        },
        containedPrimary: {
          backgroundColor: gold.fill,
          color: ink,
          // Ink at rest: 6.15:1 on gold.fill. White on hover, because ink on gold.deep is worse
          // still - though at the current gold.deep white only reaches 2.86:1, so the label is
          // legible at rest and marginal on hover. See the gold.deep note at the top of the file.
          '&:hover': { backgroundColor: gold.deep, color: '#FFFFFF' }
        },
        containedSecondary: {
          backgroundColor: ink,
          color: '#FFFFFF',
          '&:hover': { backgroundColor: gold.deep }
        },
        outlined: {
          borderColor: hairlineStrong,
          color: ink,
          '&:hover': { borderColor: ink, backgroundColor: 'transparent' }
        },
        text: { color: gold.text }
      }
    },

    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: `1px solid ${hairline}`,
          borderRadius: 2,
          backgroundImage: 'none'
        }
      }
    },

    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },

    MuiDivider: { styleOverrides: { root: { borderColor: hairline } } },

    MuiLink: {
      defaultProps: { underline: 'none' },
      styleOverrides: { root: { color: gold.text, '&:hover': { color: gold.deep } } }
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: hairlineStrong },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: gold.fill },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: gold.fill,
            borderWidth: 1
          }
        }
      }
    },

    MuiCheckbox: {
      styleOverrides: { root: { color: hairlineStrong, '&.Mui-checked': { color: gold.fill } } }
    },
    MuiRadio: {
      styleOverrides: { root: { color: hairlineStrong, '&.Mui-checked': { color: gold.fill } } }
    },
    MuiChip: { styleOverrides: { root: { borderRadius: 999, fontWeight: 600 } } },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, '&.Mui-selected': { color: ink } }
      }
    },
    MuiTabs: { styleOverrides: { indicator: { backgroundColor: gold.fill, height: 2 } } }
  }
});

// The system stack the original site rendered in - verbatim from the `body` rule that
// still ships in the live bundle's CSS. The old site loaded no webfont at all: MUI's
// default typography asks for Roboto, which this app has never bundled, so every
// MUI surface fell through to the page's inherited `body` font. The redesign then
// repointed that `body` rule at Inter, which is what silently restyled the product
// view. This constant puts it back, for the parts of the page that want it.
//
// Overriding `typography.fontFamily` alone would not do it: every variant above pins
// its own fontFamily (`heading()` sets the serif, the body variants set the sans), and
// a variant's own value wins. So rewrite the family on each variant too. Callers must
// ALSO set `fontFamily` on the wrapping element - MUI components that never declare a
// family (ListItem, TabPanel, plain Box) take theirs by inheritance, not from the theme.
export const GENERIC_SANS =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

export const withGenericSans = (outerTheme) => {
  const typography = { fontFamily: GENERIC_SANS };

  Object.keys(outerTheme.typography).forEach((key) => {
    const variant = outerTheme.typography[key];
    if (variant && typeof variant === 'object' && variant.fontFamily) {
      typography[key] = { fontFamily: GENERIC_SANS };
    }
  });

  return createTheme(outerTheme, { typography });
};

export default theme;
